<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onKeyStroke } from '@vueuse/core';
import { useGgFollowupSelection } from 'dashboard/composables/useGgFollowup';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import GgFollowupAPI from 'dashboard/api/ggFollowup';
import GgMessageRange from './GgMessageRange.vue';
import GgFollowupSupportTarget from './GgFollowupSupportTarget.vue';
import GgFollowupOperation from './GgFollowupOperation.vue';
import GgSupportSelect from 'dashboard/components-next/gg-support/GgSupportSelect.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
});
const { t, locale } = useI18n();
const { selection, close, reset, setLocked } = useGgFollowupSelection();
const request = useAbortableRequest();
const busy = request.isPending;
const context = ref(null);
const error = ref('');
const domain = ref('');
const opportunity = ref('');
const ticketReference = ref('');
const targetConfirmed = ref(false);
const preview = ref(null);
const operation = ref(null);
const hasOtherActiveTarget = computed(
  () =>
    selection.supportTicketId &&
    operation.value &&
    !['saved', 'failed'].includes(operation.value.status) &&
    (operation.value.domain !== 'support' ||
      operation.value.ticket_id !== selection.supportTicketId)
);
const targetOptions = computed(() => [
  { value: 'opportunity', label: t('GG_FOLLOWUP.OPPORTUNITY') },
  ...(context.value?.support_registration_available
    ? [{ value: 'support', label: t('GG_FOLLOWUP.SUPPORT.TITLE') }]
    : []),
]);
const selectedTicket = computed(() => {
  const reference = ticketReference.value.trim().toUpperCase();
  const matchedTicket = context.value?.support_tickets.find(
    ticket =>
      String(ticket.chamado_id) === reference ||
      ticket.ticket_code === reference
  );
  if (matchedTicket) return matchedTicket;
  if (
    context.value?.support_registration_available &&
    /^[1-9]\d*$/.test(reference) &&
    Number(reference) <= 2147483647
  ) {
    return { chamado_id: Number(reference) };
  }
  return null;
});
const validOpportunity = computed(
  () =>
    /^[1-9]\d*$/.test(opportunity.value) &&
    Number(opportunity.value) <= 2147483647
);
const canConfirm = computed(
  () =>
    !busy.value &&
    (domain.value === 'support'
      ? Boolean(selectedTicket.value)
      : domain.value === 'opportunity' && validOpportunity.value)
);
const canPrepare = computed(
  () =>
    targetConfirmed.value &&
    selection.first &&
    !selection.resolving &&
    !selection.rangeError &&
    !selection.choice &&
    !selection.target &&
    !busy.value
);
const targetLabel = computed(() => {
  if (domain.value === 'opportunity') {
    return t('GG_FOLLOWUP.OPPORTUNITY_VALUE', {
      id: context.value?.opportunity_id,
    });
  }
  const ticket = selectedTicket.value;
  return ticket
    ? [
        ticket.ticket_code,
        t('GG_FOLLOWUP.SUPPORT.ID', { id: ticket.chamado_id }),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
});
const dateLabel = value =>
  new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
const showError = exception => {
  const code = exception.response?.data?.code;
  const keys = [
    'OPPORTUNITY_REQUIRED',
    'INVALID_RANGE',
    'RANGE_TOO_LARGE',
    'INVALID_TARGET',
    'TICKET_REQUIRED',
    'TICKET_NOT_LINKED',
  ];
  error.value = t(
    `GG_FOLLOWUP.ERRORS.${keys.includes(code) ? code : 'REQUEST_FAILED'}`
  );
};
const changeTarget = () => {
  targetConfirmed.value = false;
  preview.value = null;
  error.value = '';
  reset();
};
const loadContext = async (restoreOperation = true) => {
  error.value = '';
  try {
    const response = await request.run(signal =>
      GgFollowupAPI.context(props.conversationId, {}, { signal })
    );
    if (!response) return;
    context.value = response.data;
    opportunity.value = String(response.data.opportunity_id || '');
    if (restoreOperation) {
      const restored = response.data.operation;
      const requestedTicket = selection.supportTicketId;
      const differentTicket =
        requestedTicket &&
        restored &&
        (restored.domain !== 'support' ||
          restored.ticket_id !== requestedTicket);
      operation.value =
        differentTicket && ['saved', 'failed'].includes(restored.status)
          ? null
          : restored;
    }
    if (restoreOperation && operation.value) {
      domain.value = operation.value.domain || 'opportunity';
      if (domain.value === 'support')
        ticketReference.value = String(operation.value.ticket_id);
      targetConfirmed.value = true;
    } else if (selection.supportTicketId) {
      domain.value = 'support';
      ticketReference.value = String(selection.supportTicketId);
    }
  } catch (exception) {
    showError(exception);
  }
};
const resetOperation = () => {
  operation.value = null;
  changeTarget();
  loadContext(false);
};
const reselect = () => {
  operation.value = null;
  preview.value = null;
  error.value = '';
  targetConfirmed.value = true;
  reset();
};
const confirmTarget = async () => {
  if (!canConfirm.value) return;
  error.value = '';
  if (domain.value === 'support') {
    ticketReference.value = String(selectedTicket.value.chamado_id);
    targetConfirmed.value = true;
    return;
  }
  if (Number(opportunity.value) === context.value.opportunity_id) {
    targetConfirmed.value = true;
    return;
  }
  try {
    const response = await request.run(async signal => {
      await GgFollowupAPI.setOpportunity(
        props.conversationId,
        Number(opportunity.value)
      );
      return GgFollowupAPI.context(props.conversationId, {}, { signal });
    });
    if (!response) return;
    context.value = response.data;
    targetConfirmed.value = true;
  } catch (exception) {
    showError(exception);
  }
};
const prepare = async () => {
  if (!canPrepare.value) return;
  error.value = '';
  const target = { domain: domain.value };
  const first = selection.first || selection.last;
  const last = selection.last || selection.first;
  if (domain.value === 'support')
    target.ticket_reference = ticketReference.value;
  try {
    const response = await request.run(signal =>
      GgFollowupAPI.preview(props.conversationId, first.id, last.id, target, {
        signal,
      })
    );
    if (response) {
      preview.value = response.data.payload;
      operation.value = response.data.operation;
    }
  } catch (exception) {
    showError(exception);
  }
};
watch(domain, changeTarget, { flush: 'sync' });
watch(
  [targetConfirmed, busy, preview, operation],
  () => {
    setLocked(
      !targetConfirmed.value ||
        busy.value ||
        Boolean(preview.value) ||
        Boolean(operation.value)
    );
  },
  { immediate: true, flush: 'sync' }
);
watch(
  () => props.conversationId,
  () => {
    request.abort();
    context.value = null;
    operation.value = null;
    domain.value = '';
    ticketReference.value = '';
    changeTarget();
    loadContext();
  },
  { immediate: true }
);
onKeyStroke('Escape', close);
onUnmounted(() => setLocked(false));
</script>

<template>
  <aside
    aria-labelledby="gg-followup-title"
    class="fixed bottom-0 inset-x-0 z-40 h-[45dvh] flex flex-col bg-n-surface-1 border-t border-n-weak shadow-lg md:static md:h-full md:w-[320px] md:min-w-[320px] md:border-t-0 md:border-s md:shadow-none 2xl:w-[360px] 2xl:min-w-[360px]"
  >
    <header
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak shrink-0"
    >
      <h2
        id="gg-followup-title"
        class="m-0 text-sm font-semibold text-n-slate-12"
      >
        {{ t('GG_FOLLOWUP.TITLE') }}
      </h2>
      <Button
        :aria-label="t('GG_FOLLOWUP.CLOSE')"
        :title="t('GG_FOLLOWUP.CLOSE')"
        icon="i-lucide-x"
        color="slate"
        variant="ghost"
        size="sm"
        @click="close"
      />
    </header>
    <div class="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4">
      <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <div v-if="busy && !context" class="flex justify-center py-8">
        <Spinner />
      </div>
      <Button
        v-if="error && !context"
        :label="t('GG_FOLLOWUP.RETRY')"
        color="slate"
        variant="faded"
        @click="loadContext"
      />
      <template v-if="context">
        <div class="flex flex-col gap-1 text-xs text-n-slate-11">
          <span class="font-medium text-n-slate-12">{{
            t('GG_FOLLOWUP.CONVERSATION', { id: conversationId })
          }}</span>
          <span class="break-words">{{
            t('GG_FOLLOWUP.AUTHOR', { email: context.requester.email })
          }}</span>
        </div>
        <p
          v-if="hasOtherActiveTarget"
          role="status"
          class="m-0 rounded-lg bg-n-amber-3 p-3 text-xs text-n-amber-11"
        >
          {{ t('GG_SUPPORT_HEADER.ACTIVE_ANALYSIS') }}
        </p>
        <GgFollowupOperation
          v-if="operation"
          :key="operation.request_id"
          :conversation-id="conversationId"
          :operation="operation"
          @update="operation = $event"
          @reset="resetOperation"
          @reselect="reselect"
          @reload="loadContext()"
        />
        <template v-else-if="!targetConfirmed">
          <GgSupportSelect
            id="gg-followup-domain"
            v-model="domain"
            :label="t('GG_FOLLOWUP.TARGET_LABEL')"
            :options="targetOptions"
            :disabled="busy"
          />
          <p v-if="!domain" class="m-0 text-sm text-n-slate-11">
            {{ t('GG_FOLLOWUP.CHOOSE_TARGET') }}
          </p>
          <template v-if="domain === 'opportunity'">
            <Input
              v-model="opportunity"
              :label="t('GG_FOLLOWUP.OPPORTUNITY')"
              :placeholder="t('GG_FOLLOWUP.OPPORTUNITY_PLACEHOLDER')"
              :disabled="busy"
              inputmode="numeric"
              @keydown.enter="confirmTarget"
            />
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('GG_FOLLOWUP.IDENTITY_HINT') }}
            </p>
          </template>
          <GgFollowupSupportTarget
            v-if="domain === 'support'"
            v-model="ticketReference"
            :tickets="context.support_tickets"
            :allow-id="context.support_registration_available"
            :disabled="busy"
            @confirm="confirmTarget"
          />
        </template>
        <template v-else>
          <div class="flex flex-col gap-2 rounded-lg border border-n-weak p-3">
            <span class="text-xs text-n-slate-11">{{
              t('GG_FOLLOWUP.SELECTED_TARGET')
            }}</span>
            <span class="text-sm font-medium text-n-slate-12">{{
              targetLabel
            }}</span>
            <span
              v-if="domain === 'support' && selectedTicket?.customer_name"
              class="text-xs break-words text-n-slate-11"
              >{{ selectedTicket.customer_name }}</span
            >
            <Button
              :label="t('GG_FOLLOWUP.CHANGE_TARGET')"
              color="slate"
              variant="ghost"
              size="sm"
              class="self-start"
              :disabled="busy"
              @click="changeTarget"
            />
          </div>
          <p
            v-if="
              domain === 'support' && !context.support_registration_available
            "
            role="note"
            class="m-0 rounded-lg bg-n-amber-3 p-3 text-xs text-n-amber-11"
          >
            {{ t('GG_FOLLOWUP.SUPPORT.PENDING') }}
          </p>
          <p
            role="note"
            class="m-0 rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11"
          >
            {{ t('GG_FOLLOWUP.TEXT_ONLY') }}
          </p>
          <GgMessageRange
            v-if="!preview"
            :conversation-id="conversationId"
            :disabled="busy"
          />
          <template v-else>
            <p class="m-0 text-sm font-medium">
              {{
                t('GG_FOLLOWUP.PREVIEW_TARGET', {
                  count: preview.selection.message_count,
                  target: targetLabel,
                })
              }}
            </p>
            <div
              v-for="message in preview.messages"
              :key="message.id"
              class="rounded-lg border border-n-weak p-3"
            >
              <p class="mb-1 text-xs text-n-slate-11">
                {{
                  t('GG_FOLLOWUP.MESSAGE_META', {
                    date: dateLabel(message.created_at),
                    author:
                      message.author.name || t('GG_FOLLOWUP.UNKNOWN_AUTHOR'),
                  })
                }}
              </p>
              <p class="m-0 text-sm whitespace-pre-wrap break-words">
                {{ message.text }}
              </p>
            </div>
            <p
              role="status"
              class="m-0 rounded-lg bg-n-amber-3 p-3 text-xs text-n-amber-11"
            >
              {{ t('GG_FOLLOWUP.PENDING') }}
            </p>
          </template>
        </template>
      </template>
    </div>
    <footer
      v-if="!operation"
      class="flex flex-col gap-2 p-4 border-t border-n-weak shrink-0"
    >
      <Button
        v-if="!targetConfirmed"
        :label="t('GG_FOLLOWUP.CONFIRM_TARGET')"
        :disabled="!canConfirm"
        :is-loading="busy"
        @click="confirmTarget"
      />
      <Button
        v-else-if="!preview"
        :label="t('GG_FOLLOWUP.PREPARE')"
        :is-loading="busy"
        :disabled="!canPrepare"
        @click="prepare"
      />
      <template v-else>
        <Button
          :label="t('GG_FOLLOWUP.CHANGE_SELECTION')"
          color="slate"
          variant="faded"
          @click="preview = null"
        />
        <Button :label="t('GG_FOLLOWUP.ANALYZE')" disabled />
      </template>
    </footer>
  </aside>
</template>
